import React, {useContext, useEffect, useState} from "react";
import {Button, Dialog, DialogBody, DialogFooter, FormGroup, HTMLSelect, Label, NumericInput, Tooltip} from "@blueprintjs/core";
import PropTypes from "prop-types";
import {AppConfigContext} from "../../context/AppConfigContextProvider";
import {SelectedOrganizationContext} from "../../context/SelectedOrganizationContextProvider";
import OTADialog from "./OTADialog";
import {Alert} from "react-bootstrap";
import { DeviceListContext } from "../../context/DeviceListContextProvider";

export default function BaseAppParamDialog(
    {
        appParams, device, deviceMutation, isDialogOpen, closeDialog, initialSettings = {}, confirmedSettings = {},
        extraButtons, nodeFirmware, neuralNetwork, wisunFirmware, gatewayScript
    }
) {
    let title;

    // Initializing the current OTA values
    let otas = {}

    // Assign the corresponding OTA Fields to specific device
    if(device.deviceType === 'Sensor'){
        otas = { 
            neuralNetworkId: device.json.neuralNetworkId, 
            nodeFirmwareId: device.json.nodeFirmwareId, 
            wisunFirmwareId: device.json.wisunFirmwareId 
        }
        title = 'Sensor Settings';
    } else if(device.deviceType === 'Gateway'){
        otas = { 
            gatewayScriptId: device.json.gatewayScriptId,  
            wisunFirmwareId: device.json.wisunFirmwareId 
        }
        title = 'Gateway Settings'
    }

    const [ showAlert, setShowAlert ] = useState(false)
    const [ otaValue, setOtaValue ] = useState(otas)

    const [updatedSettings, setUpdatedSettings] = useState(initialSettings);
    const [initialValue, setInitialValue] = useState(initialSettings);

    const debug = false;

    const {organization} = useContext(SelectedOrganizationContext);

    const devAdmin = organization.hasRole("admin-dev");
    const availableParams = appParams.filter(param => devAdmin || param.userEditable);

    // Submit form changes
    function save(){
        // Invalid show Alert
        if (!isUpperLowerWisunChannelsValid(updatedSettings['Rohm_Wisun_Freq_Hop_Channel_Upper_Range'], updatedSettings['Rohm_Wisun_Freq_Hop_Channel_Lower_Range'])){
            setShowAlert(true)
        } else {
            // Valid input
            if(showAlert){
                setShowAlert(false)
            }

            let otaChanges = {}
            for(const [key, val] of Object.entries(otaValue)){
                if (otas && val !== otas[key]){
                    // If 'Unset' (value -1) => clear Sensor/Gateway portion of OTA ID
                    if (val === -1){
                        otaChanges[key]= null
                    } else{
                        otaChanges[key]= val
                    }
                }
            }
    
            // Change String Float back to Number
            for (const [key, value] of Object.entries(updatedSettings)){
                if (typeof(value) === 'string'){
                    updatedSettings[key] = Number(value)
                }
            }
    
            deviceMutation.mutate({
                deviceId: device.id,
                action: "update",
                appParams: updatedSettings,
                otaFiles: otaChanges
            });
            setInitialValue(updatedSettings);
            closeDialog();
        }
    }

    function setParamValue(param, value){
        setUpdatedSettings({
            ...updatedSettings,
            [param.name]: value
        });
    }

    function cancel(){
        // Reset all the values
        setShowAlert(false)
        setOtaValue(otas)
        setUpdatedSettings(initialSettings);
        closeDialog();
    }

    function reset() {
        const resets = availableParams.reduce((acc, value) => {
            if (value.name && value.valueAttributes.default_value){
                acc[value.name] = getDefaultValue(value);
            }
            return acc
        }, {});
        setUpdatedSettings(resets);
    }

    function isDefaultValue(){
        let defaultParams = true;
        availableParams.map(param => {
            if(getParamValue(param) !== param.valueAttributes.default_value){
                defaultParams = false;
            }
        });
        return defaultParams;
    }

    // This function check is the "Rohm Wisun Upper Channel Range" and "Rohm Wisun Lower Channel Range" is set correctly
    // Lower Channel can only be equal or lower to Upper Channel
    function isUpperLowerWisunChannelsValid(upper_channel_range, lower_chanel_range){
        if (lower_chanel_range > upper_channel_range){
            return false
        } else {
            return true
        }
    }

    function isInitialValue(){
        const initialParams = false;
        return initialParams;
    }

    function getDefaultValue(param){
        return param.valueAttributes.default_value;
    }

    function getParamValue(param){
        if (updatedSettings[param.name] !== undefined) {
            return updatedSettings[param.name];
        }
        return getDefaultValue(param);
    }

    function getParamConfirmedValue(param){
        return confirmedSettings[param.name] || getDefaultValue(param);
    }
    
    function getInvalidReason(param){
        const value = getParamValue(param);
        const min = getParamMin(param);
        const max = getParamMax(param);
        if(param.valueType === 'FLOAT' || param.valueType === 'INTEGER'){
            if(value < min){
                return `Value must be greater than or equal to ${min}`;
            }
            if(value > max){
                return `Value must be less than or equal to ${max}`;
            }
        }
        return null;
    }

    function getDescriptiveConfirmedValue(param){
        const value = getParamConfirmedValue(param);
        if (param.valueType === 'ENUM') {
            const enumValue = param.enumValues.find(v => v.value === value);
            return enumValue ? enumValue.name : value;
        }
        return value;
    }

    // Special Case for Geo Trigger Threshold description
    function geoTriggerThresholdTable(param){
        // Recommended table parameters for this app param
        if (param?.name === 'Geo_Trigger_Threshold'){
            return <>
                <span> Use the following settings as a starting point:</span>
                <div style={{display:'flex', flexDirection:'row', marginBottom:'5px'}}>
                    <div style={{display:'flex', flexDirection:'column', paddingRight:'5px'}}>
                        <span>Quiet region:</span>
                        <span>60m from a road:</span>
                        <span>40m from a road:</span>
                        <span>30m from a road:</span>
                    </div>
                    <div style={{display:'flex', flexDirection:'column',paddingLeft:'5px'}}>
                        <span>0.96</span>
                        <span>0.98</span>
                        <span>1.05</span>
                        <span>1.20</span>
                    </div>
                </div>
            </>
        }
        else{
            return null
        }
    }

    // Description
    function getHelperText(param){
        return <span>
            { param.description }
            {geoTriggerThresholdTable(param)}
            {param.valueType === 'FLOAT' && <p> <b>Range</b>: {getParamMin(param)} - {getParamMax(param)}</p>}
            {param.valueType === 'INTEGER' && <p> <b>Range</b>: {getParamMin(param)} - {getParamMax(param)}</p>}

            { getParamValue(param) != getParamConfirmedValue(param) && <p><b>Warning the current value on the device is {getDescriptiveConfirmedValue(param)}. Target value is {getParamValue(param)}</b></p>}
            { !isValid(param) &&  <p><b>{getInvalidReason(param)}</b></p>}
        </span>;
    }

    // Type of variable
    function getLabelInfo(param){
        return <span>
            {param.valueType === 'FLOAT' && <span> (Float) </span>}
            {param.valueType === 'INTEGER' && <span> (Integer) </span>}
            {param.valueType === 'ENUM' && <span> (Enum)</span>}
        </span>;
    }

    function getParamMax(param){
        if(devAdmin && param.valueAttributes.firmware_max !== undefined){
            return param.valueAttributes.firmware_max;
        } else {
            return param.valueAttributes.max_value;
        }
    }

    function getParamMin(param){
        if(devAdmin && param.valueAttributes.firmware_min !== undefined) {
            return param.valueAttributes.firmware_min;
        } else {
            return param.valueAttributes.min_value;
        }
    }

    // Function to get decimal step from a FLOAT or INTEGER
    // i.e. 0.0314 => 0.0001, 135 => 1
    function getDecimalStep(num) {
        if (num === 0) return 1; // Special case for zero
        const decimalPlaces = (num.toString().split('.')[1] || "").length;
        return Math.pow(10, (-1 * decimalPlaces));
    }

    // Set Step Size from param default value
    function getStepSize(param){
        if(param.valueAttributes.step_size !== undefined){
            return param.valueAttributes.step_size;
        }
        return getDecimalStep(getDefaultValue(param))
    }

    function getMinorStepSize(param){
        if(param.valueAttributes.minor_step_size !== undefined){
            return param.valueAttributes.minor_step_size;
        }
        return getStepSize(param);
    }

    function getMajorStepSize(param){
        if(param.valueAttributes.major_step_size !== undefined){
            return param.valueAttributes.major_step_size;
        }
        return getStepSize(param) * 10;
    }

    function isValid(param){
        const value = getParamValue(param);
        const min = getParamMin(param);
        const max = getParamMax(param);

        if(param.valueType === 'FLOAT' || param.valueType === 'INTEGER'){
            return (value >= min && value <= max);
        }

        return true;
    }

    function isChanged(params) {
        //Check if there is any change in the value compare to initial value
        let change = false;
        params.map(param => {
            if(initialValue[param.name] === undefined){
                if(getParamValue(param) !== getDefaultValue(param)){
                    change = true;
                }
            }else{
                if(getParamValue(param) !== initialValue[param.name]){
                    change = true;
                }
            }
        });

        return change;
    }

    // Check if the form has any changes and if the form values are valid (with 'isValid()')
    function isFormValid(){
        const changed = isChanged(availableParams)

        let otaChanged = false 
        for(const [key, val] of Object.entries(otaValue)){
            if(otas && val !== otas[key]){
                otaChanged = true
            }
        }

        return availableParams.every(isValid) && changed || otaChanged;
    }

    // Handle OTA Values changes in the form
    function handleOTAChange(newVal){
        setOtaValue(prevVal => ({ ...prevVal, [newVal.target.name]: Number(newVal.target.value) }))
    }

    return <Dialog title={title} icon="info-sign" isOpen={isDialogOpen} onClose={cancel}>
        <DialogBody>
            {device.deviceType === 'Sensor' ? <AlertSensor sensor={device}/> : null}
            {devAdmin ? 
            (device.deviceType === 'Sensor' ?
                <OTADialog  
                    nodeFirmware={nodeFirmware}
                    neuralNetwork={neuralNetwork}
                    sensorWisunFirmware={wisunFirmware}
                    gatewayWisunFirmware={null}
                    gatewayScript={gatewayScript}
                    currentOTA={otaValue}
                    onOTAChange={handleOTAChange}
                    hierarchy={false}
                /> 
                :
                <OTADialog  
                    nodeFirmware={nodeFirmware}
                    neuralNetwork={neuralNetwork}
                    sensorWisunFirmware={null}
                    gatewayWisunFirmware={wisunFirmware}
                    gatewayScript={gatewayScript}
                    currentOTA={otaValue}
                    onOTAChange={handleOTAChange}
                    hierarchy={false}
                /> 
            )
            : null}
            {
                availableParams.map((param, index) =>
                    <>
                    <Tooltip key={`${param.id}-shortDescription`} content={param.shortDescription} position='top'> 
                        {devAdmin ?
                            <Label htmlFor={`input-${param.id}`}>
                                {param.name}
                                {devAdmin ? <span className="bp5-text-muted">{getLabelInfo(param)}</span> : null}
                            </Label>
                            :
                            <Label htmlFor={`input-${param.id}`}>
                                {param.shortDescription}
                                {devAdmin ? <span className="bp5-text-muted">{getLabelInfo(param)}</span> : null}
                            </Label>
                        }
                    </Tooltip>
                    <FormGroup
                        key={param.id}
                        helperText={getHelperText(param)}
                        className={isValid(param) ? "" : "form-invalid"}
                    >
                        {   (param.valueType === 'FLOAT' || param.valueType === 'INTEGER') &&
                            <NumericInput
                                id={`input-${param.id}`}
                                placeholder=""
                                value={getParamValue(param)}
                                onValueChange={(value, s) => {
                                    // Check if contains number and dot (for float)
                                    if (/^(\d+\.?\d*|\.\d*)?$/.test(s) && param.valueType === 'FLOAT'){
                                        // Float handler
                                        setParamValue(param, s);
                                    } else {
                                        // Integer handler
                                        setParamValue(param, Number(s));
                                    }
                                }}
                                min={getParamMin(param)}
                                max={getParamMax(param)}
                                stepSize={getStepSize(param)}
                                minorStepSize={getMinorStepSize(param)}
                                majorStepSize={getMajorStepSize(param)}
                            />
                        }
                        {param.valueType === 'ENUM' &&
                            <HTMLSelect
                                id={`input-${param.id}`}
                                onChange={evt => setParamValue(param, parseInt(evt.target.value))}
                                value={`${getParamValue(param)}`}
                                fill={true}
                                options={param.enumValues.map(v => ({
                                    value: `${v.value}`,
                                    label: `${v.name}`
                                }))}
                            />
                        }
                    </FormGroup>
                    {showAlert && (param.name==='Rohm_Wisun_Freq_Hop_Channel_Upper_Range' || param.name==='Rohm_Wisun_Freq_Hop_Channel_Lower_Range') ? 
                        <Alert variant="danger" style={{position:'relative', bottom:'10px', zIndex:'999'}}>
                            The lower frequency value cannot be higher than the upper frequency value. Please adjust the range accordingly.
                        </Alert> 
                    : null}
                    </>
                )
            }

            {
                debug && <>
                <pre>{JSON.stringify(updatedSettings, null, 2)}</pre>
                </>
            }


        </DialogBody>
        {showAlert ? 
            <Alert variant="danger" style={{position:'sticky', bottom:'10px', zIndex:'999'}}>
                Invalid frequency range: the <b>Lower Range of the Wi-SUN Frequency Channel</b> exceeds the <b>Upper Range of the Wi-SUN Frequency Channel</b>. 
                Please ensure the Lower Range is less than or equal to the Upper Range.
            </Alert> 
        : null}
        <DialogFooter actions={<>
            {extraButtons}
            {devAdmin ? <Button disabled={isDefaultValue()} intent="none" text="Set to Default" onClick={reset} /> : null}
            <Button intent="none" text="Cancel" onClick={cancel} />
            <Button intent="primary" text="Save" disabled={!isFormValid()} onClick={() => save()} />
            </>}
        />
    </Dialog>
}

BaseAppParamDialog.propTypes = {
    appParams: PropTypes.array,
    device: PropTypes.object,
    deviceMutation: PropTypes.object,
    isDialogOpen: PropTypes.bool,
    closeDialog: PropTypes.func,
    initialSettings: PropTypes.object,
    confirmedSettings: PropTypes.object,
    extraButtons: PropTypes.element,
    nodeFirmware: PropTypes.array,
    neuralNetwork: PropTypes.array,
    wisunFirmware: PropTypes.array,
    gatewayScript: PropTypes.array,
}

// Alert Box to tell user that Sensor has been sending packets via an 'away' gateway (gateway that is not the assigned one)
// Potential issue to warn user => App Params will not get update/delivered if it's not connected to 'home' gateway (currently assigned gateway)
function AlertSensor ({sensor}){
    const {gateways, gatewayQuery} = useContext(DeviceListContext)
    const {organization} = useContext(SelectedOrganizationContext)

    const currentGateway = gatewayQuery.data.gateways.filter(gateway => gateway.json.id === sensor.status.json.gatewayId)[0];
    const activeZoneId = currentGateway.activeZones.map(zone => zone.zoneId)
    const currentZoneName = organization.zones
        .filter(zone => activeZoneId.includes(zone.id))
        .map(zone => zone.name)
        .join(', ')

    const homeGatewayIds = gateways.map(gateway => gateway.id)

    // sensor sending via 'away' zone/gateway => Alert User
    if (!homeGatewayIds.includes(currentGateway.id)){
        return (
            <Alert variant="danger" style={{position:'sticky', top:'10px', zIndex:'999'}}>      
                This sensor is currently transmitting data through <b>{currentZoneName} ({currentGateway.deviceId})</b>.
                Any changes to its settings may <b>not</b> take effect until it reconnects with its assigned gateway.
                You may want to either move the sensor to a <b>different</b> Zone/Gateway or improve the signal strength to its <b>designated</b> gateway.
            </Alert>
        )
    } else {
        return null
    }
}

AlertSensor.propTypes = {
    sensor: PropTypes.object
}